import { Box, DialogContent, IconButton, InputAdornment, Typography } from '@mui/material'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import { FormikProvider, useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import { Close } from '@mui/icons-material'
import React from 'react'

import { vehicleSchema } from 'views/Authorized/views/Customers/schema'
import ContainedButton from 'shared/components/Buttons/ContainedButton'
import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import { sortAlphabetical } from 'shared/utils/helpers/sorters'
import { VehicleType } from 'shared/store/useCustomers/types'
import SelectField from 'shared/components/SelectField'
import InputField from 'shared/components/InputField'

interface Props {
   onAdd: (vehicle: VehicleType, customerUuid: string) => void
   onEdit: (vehicle: VehicleType, customerUuid: string, vehicleUuid: string) => void
   onClose: () => void
   vehicleUuid?: string
   vehicleDetails?: VehicleType
   customerUuid: string
   isAdding: boolean
   isEditing: boolean
}

export default function VehicleDetailsModal({
   onAdd,
   isAdding,
   onClose,
   onEdit,
   isEditing,
   vehicleDetails,
   vehicleUuid,
   customerUuid,
}: Props) {
   const { t } = useTranslation(['TRANSLATION', 'CUSTOMERS'])
   // const [manualChange, setManualChange] = useState(!vehicleDetails)
   // const [vehicleChoices, setVehicleChoices] = useState<{
   //    brands: string[]
   //    models: string[]
   //    trims: string[]
   // }>({
   //    brands: [],
   //    models: [],
   //    trims: [],
   // })

   const fuelTypeOptions: OptionType[] = [
      {
         value: 'B',
         name: t(`CUSTOMERS:modal.vehicleDetails.fuelTypes.gasoline`),
      },
      {
         value: 'B+G',
         name: t(`CUSTOMERS:modal.vehicleDetails.fuelTypes.gasolineAndLpg`),
      },
      {
         value: 'B+C',
         name: t(`CUSTOMERS:modal.vehicleDetails.fuelTypes.gasolineAndCNG`),
      },
      {
         value: 'D',
         name: t(`CUSTOMERS:modal.vehicleDetails.fuelTypes.diesel`),
      },
      {
         value: 'H',
         name: t(`CUSTOMERS:modal.vehicleDetails.fuelTypes.hybrid`),
      },
      {
         value: 'E',
         name: t(`CUSTOMERS:modal.vehicleDetails.fuelTypes.electric`),
      },
   ]

   const gearboxOptions: OptionType[] = [
      {
         value: 'MT',
         name: t('CUSTOMERS:modal.vehicleDetails.gearbox.mt'),
      },
      {
         value: 'AT',
         name: t('CUSTOMERS:modal.vehicleDetails.gearbox.at'),
      },
   ]

   const carTypeOptions: OptionType[] = [
      {
         value: 'sedan',
         name: t(`CUSTOMERS:modal.vehicleDetails.body.sedan`),
      },
      {
         value: 'kombi',
         name: t(`CUSTOMERS:modal.vehicleDetails.body.kombi`),
      },
      {
         value: 'hatchback',
         name: t(`CUSTOMERS:modal.vehicleDetails.body.hatchback`),
      },
      {
         value: 'suv',
         name: t(`CUSTOMERS:modal.vehicleDetails.body.suv`),
      },
      {
         value: 'crossover',
         name: t(`CUSTOMERS:modal.vehicleDetails.body.crossover`),
      },
      {
         value: 'coupe',
         name: t(`CUSTOMERS:modal.vehicleDetails.body.coupe`),
      },
      {
         value: 'cabrio',
         name: t(`CUSTOMERS:modal.vehicleDetails.body.cabrio`),
      },
      {
         value: 'van/minivan',
         name: t(`CUSTOMERS:modal.vehicleDetails.body.van/minivan`),
      },
      {
         value: 'pickup',
         name: t(`CUSTOMERS:modal.vehicleDetails.body.pickup`),
      },
      {
         value: 'liftback',
         name: t(`CUSTOMERS:modal.vehicleDetails.body.liftback`),
      },
   ]

   const formik = useFormik<VehicleType>({
      initialValues: vehicleDetails
         ? { ...vehicleDetails }
         : {
              model: '',
              brand: '',
              engineCapacity: '',
              serie: '',
              fuelType: 'B',
              carType: 'sedan',
              transmissionType: 'MT',
              totalPower: '',
              productionYear: '',
              description: '',
              engine: '',
              vin: '',
              registrationNumber: '',
           },
      onSubmit: () => {
         if (!!vehicleDetails && !!vehicleUuid) {
            onEdit(
               {
                  ...values,
                  registrationNumber: values.registrationNumber ? values.registrationNumber : null,
                  vin: values.vin ? values.vin : null,
               },
               customerUuid,
               vehicleUuid
            )
         } else {
            onAdd(
               {
                  ...values,
                  registrationNumber: values.registrationNumber ? values.registrationNumber : null,
                  vin: values.vin ? values.vin : null,
               },
               customerUuid
            )
         }
      },
      validationSchema: vehicleSchema,
   })

   const { values, handleSubmit } = formik

   function generateYears() {
      const currentYear = new Date().getFullYear()

      return Array.from({ length: currentYear - 1950 + 1 }, (_, index) => 1950 + index)
   }

   // useEffect(() => {
   //    if (manualChange) {
   //       getBrandsService(customerUuid).then((res) => {
   //          setVehicleChoices({
   //             brands: res.data,
   //             models: [],
   //             trims: [],
   //          })
   //       })
   //    }
   // }, [])

   // useEffect(() => {
   //    if (vehicleDetails) {
   //       getBrandsService(customerUuid).then((brands) => {
   //          getModelsService(customerUuid, vehicleDetails.brand).then((models) => {
   //             getTrimsService(
   //                customerUuid,
   //                vehicleDetails.brand,
   //                vehicleDetails.model,
   //                vehicleDetails.productionYear ?? undefined
   //             ).then((trims) => {
   //                setVehicleChoices({
   //                   brands: brands.data,
   //                   models: models.data,
   //                   trims: trims.data,
   //                })
   //             })
   //          })
   //       })
   //    }
   // }, [vehicleDetails])

   // useEffect(() => {
   //    if (!values.brand) return

   //    if (manualChange) {
   //       setFieldValue('model', '', false)
   //       setFieldValue('engine', '', false)
   //    }

   //    getModelsService(customerUuid, values.brand).then((res) => {
   //       setVehicleChoices((prev) => ({
   //          brands: prev.brands,
   //          models: res.data,
   //          trims: [],
   //       }))
   //    })
   // }, [values.brand])

   // useEffect(() => {
   //    if (!values.brand || !values.model || !manualChange) return

   //    getTrimsService(
   //       customerUuid,
   //       values.brand,
   //       values.model,
   //       values.productionYear ? values.productionYear : undefined
   //    ).then((res) => {
   //       setVehicleChoices((prev) => ({
   //          ...prev,
   //          trims: res.data,
   //       }))
   //    })
   // }, [values.model, values.productionYear])

   // useEffect(() => {
   //    if (manualChange) {
   //       setFieldValue('engine', '')
   //    }
   // }, [values.model])

   // useEffect(() => {
   //    setManualChange(true)
   // }, [values.brand, values.model, values.engine])

   return (
      <>
         <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
               <Typography variant="h4">
                  {vehicleDetails
                     ? t('CUSTOMERS:modal.vehicleDetails.editTitle', {
                          vehicleName: `${vehicleDetails.brand} ${vehicleDetails.model}`,
                       })
                     : t('CUSTOMERS:modal.vehicleDetails.title')}
               </Typography>
               <IconButton onClick={onClose}>
                  <Close />
               </IconButton>
            </Box>
         </DialogTitle>
         <DialogContent>
            <FormikProvider value={formik}>
               <Box sx={{ display: 'flex', py: '8px', flexDirection: 'column', gap: '8px' }}>
                  <Typography variant="body1" color="primary">
                     {t('CUSTOMERS:modal.vehicleDetails.vehicle')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: '8px', flexDirection: { lg: 'row', md: 'row', xs: 'column' } }}>
                     <InputField name="brand" fullWidth label={t('CUSTOMERS:modal.vehicleDetails.brand')} />
                     <InputField name="model" fullWidth label={t('CUSTOMERS:modal.vehicleDetails.model')} />
                     <InputField name="serie" fullWidth label={t('CUSTOMERS:modal.vehicleDetails.serie')} />
                  </Box>
                  <Typography variant="body1" color="primary">
                     {t('CUSTOMERS:modal.vehicleDetails.registration')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: '8px', flexDirection: { lg: 'row', md: 'row', xs: 'column' } }}>
                     <InputField
                        slotProps={{
                           htmlInput: {
                              maxLength: 10,
                           },
                        }}
                        required
                        name="registrationNumber"
                        fullWidth
                        label={t('CUSTOMERS:modal.vehicleDetails.registrationNumber')}
                     />
                     <InputField required name="vin" fullWidth label="VIN" />
                  </Box>
                  <Typography variant="body1" color="primary">
                     {t('CUSTOMERS:modal.vehicleDetails.engineAndPerformance')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: '8px', flexDirection: { lg: 'row', md: 'row', xs: 'column' } }}>
                     <InputField name="engine" fullWidth label={t('CUSTOMERS:modal.vehicleDetails.engine')} />
                     <InputField
                        name="engineCapacity"
                        slotProps={{
                           input: {
                              endAdornment: (
                                 <InputAdornment position="end">
                                    <span>
                                       cm<sup>3</sup>
                                    </span>
                                 </InputAdornment>
                              ),
                           },
                        }}
                        fullWidth
                        label={t('CUSTOMERS:modal.vehicleDetails.engineCapacity')}
                     />
                     <InputField
                        name="totalPower"
                        slotProps={{
                           input: {
                              endAdornment: <InputAdornment position="end">kW</InputAdornment>,
                           },
                        }}
                        fullWidth
                        label={t('CUSTOMERS:modal.vehicleDetails.totalPower')}
                     />
                     <SelectField
                        fullWidth
                        inputVariant="outlined"
                        name="fuelType"
                        label={t('CUSTOMERS:modal.vehicleDetails.fuelType')}
                        options={fuelTypeOptions.sort((a, b) => sortAlphabetical(a.name, b.name))}
                     />
                  </Box>
                  <Typography variant="body1" color="primary">
                     {t('CUSTOMERS:modal.vehicleDetails.specifications')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: '8px', flexDirection: { lg: 'row', md: 'row', xs: 'column' } }}>
                     <SelectField
                        fullWidth
                        name="productionYear"
                        inputVariant="outlined"
                        label={t('CUSTOMERS:modal.vehicleDetails.productionYear')}
                        options={generateYears()
                           .reverse()
                           .map((y) => ({ value: y.toString(), name: y.toString() }))}
                     />
                     <SelectField
                        fullWidth
                        name="carType"
                        inputVariant="outlined"
                        label={t('CUSTOMERS:modal.vehicleDetails.carType')}
                        options={carTypeOptions.sort((a, b) => sortAlphabetical(a.name, b.name))}
                     />
                     <SelectField
                        fullWidth
                        name="transmissionType"
                        inputVariant="outlined"
                        label={t('CUSTOMERS:modal.vehicleDetails.transmissionType')}
                        options={gearboxOptions}
                     />
                  </Box>
                  <Typography variant="body1" color="primary">
                     {t('CUSTOMERS:modal.vehicleDetails.additionalInformations')}
                  </Typography>
                  <InputField
                     name="description"
                     label={t('CUSTOMERS:modal.vehicleDetails.description')}
                     multiline
                     rows={2}
                     fullWidth
                  />
               </Box>
            </FormikProvider>
         </DialogContent>
         <DialogActions>
            <OutlinedButton variant="outlined" disabled={isAdding || isEditing} onClick={onClose}>
               {t('TRANSLATION:cancel')}
            </OutlinedButton>
            <ContainedButton variant="contained" disabled={isAdding || isEditing} onClick={() => handleSubmit()}>
               {t('TRANSLATION:save')}
            </ContainedButton>
         </DialogActions>
      </>
   )
}
